import { CropFree, Gesture } from '@mui/icons-material';
import { Box, IconButton, Tooltip } from '@mui/material';
import { useRef, useState } from 'react';
import { CropRect, EditorTool, LassoPoint, SegmentationData } from './types';
import { CropTool } from './CropTool';
import { LassoTool } from './LassoTool';
import { SegmentTool } from './SegmentTool';

export const MAX_CANVAS_WIDTH = 600;
export const MAX_CANVAS_HEIGHT = 450;

type ToolSidebarProperties = {
  activeTool: EditorTool;
  onToolChange: (tool: EditorTool) => void;
};

const ToolSidebar = ({ activeTool, onToolChange }: ToolSidebarProperties) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.5 }}>
    <Tooltip title='Rectangle crop' placement='left'>
      <IconButton
        size='small'
        onClick={() => onToolChange('smartCrop')}
        sx={{
          bgcolor: activeTool === 'smartCrop' ? 'action.selected' : undefined,
          border: '1px solid',
          borderColor: activeTool === 'smartCrop' ? 'secondary.main' : 'divider',
          borderRadius: 1,
        }}
      >
        <CropFree sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
    <Tooltip title='Freehand lasso' placement='left'>
      <IconButton
        size='small'
        onClick={() => onToolChange('lasso')}
        sx={{
          bgcolor: activeTool === 'lasso' ? 'action.selected' : undefined,
          border: '1px solid',
          borderColor: activeTool === 'lasso' ? 'secondary.main' : 'divider',
          borderRadius: 1,
        }}
      >
        <Gesture sx={{ fontSize: 20 }} />
      </IconButton>
    </Tooltip>
  </Box>
);

type ImageEditorCanvasProperties = {
  file: File;
  activeTool: EditorTool;
  cropRect: CropRect | undefined;
  lassoPoints: LassoPoint[];
  segmentation: SegmentationData | undefined;
  selectedRegionIds: Set<number>;
  onCropChange: (rect: CropRect) => void;
  onCropEnd: (rect: CropRect) => void;
  onLassoPointsChange: (points: LassoPoint[]) => void;
  onLassoExport: (file: File) => void;
  onRegionClick: (regionId: number) => void;
  onToolChange: (tool: EditorTool) => void;
  isSegmenting: boolean;
  numberSegments: number;
  sensitivity: number;
  onNumberSegmentsChange: (value: number) => void;
  onSensitivityChange: (value: number) => void;
};

export const ImageEditorCanvas = ({
  file,
  activeTool,
  cropRect,
  lassoPoints,
  segmentation,
  selectedRegionIds,
  onCropChange,
  onCropEnd,
  onLassoPointsChange,
  onLassoExport,
  onRegionClick,
  onToolChange,
  isSegmenting,
  numberSegments,
  sensitivity,
  onNumberSegmentsChange,
  onSensitivityChange,
}: ImageEditorCanvasProperties) => {
  const imageUrlReference = useRef('');
  const [naturalSize, setNaturalSize] = useState({ width: 0, height: 0 });
  const [currentFile, setCurrentFile] = useState<File>();

  if (currentFile !== file) {
    setCurrentFile(file);
    setNaturalSize({ width: 0, height: 0 });
    URL.revokeObjectURL(imageUrlReference.current);
    imageUrlReference.current = URL.createObjectURL(file);
  }

  const imageUrl = imageUrlReference.current;

  const scale =
    naturalSize.width > 0
      ? Math.min(MAX_CANVAS_WIDTH / naturalSize.width, MAX_CANVAS_HEIGHT / naturalSize.height, 1)
      : 0;
  const containerWidth = Math.round(naturalSize.width * scale);
  const containerHeight = Math.round(naturalSize.height * scale);

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    setNaturalSize({ width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight });
  };

  if (containerWidth === 0) {
    return (
      <img src={imageUrl} onLoad={handleImageLoad} style={{ visibility: 'hidden', position: 'absolute' }} alt='' />
    );
  }

  const isSegment = activeTool === 'segment';

  return (
    <Box sx={{ position: 'relative', pl: '42px' }}>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          visibility: isSegment ? 'hidden' : 'visible',
        }}
      >
        <ToolSidebar activeTool={activeTool} onToolChange={onToolChange} />
      </Box>

      <Box>
        <Box sx={{ position: 'relative' }}>
          {/* SegmentTool always renders to reserve stable height (image + sliders) */}
          <Box sx={{ visibility: isSegment ? 'visible' : 'hidden' }}>
            <SegmentTool
              imageUrl={imageUrl}
              segmentation={segmentation}
              selectedRegionIds={selectedRegionIds}
              onRegionClick={onRegionClick}
              isSegmenting={isSegmenting}
              numberSegments={numberSegments}
              sensitivity={sensitivity}
              onNumberSegmentsChange={onNumberSegmentsChange}
              onSensitivityChange={onSensitivityChange}
              containerWidth={containerWidth}
              containerHeight={containerHeight}
            />
          </Box>

          {!isSegment && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: containerWidth,
                height: containerHeight,
                overflow: 'hidden',
                borderRadius: 1,
                bgcolor: 'background.default',
                cursor: activeTool === 'lasso' ? 'crosshair' : 'default',
                ...(activeTool === 'lasso' && { '& *': { cursor: 'crosshair !important' } }),
              }}
            >
              {activeTool === 'smartCrop' && (
                <>
                  <img
                    src={imageUrl}
                    alt='Editor'
                    style={{
                      width: containerWidth,
                      height: containerHeight,
                      objectFit: 'fill',
                      display: 'block',
                    }}
                  />
                  <CropTool
                    cropRect={cropRect}
                    onCropChange={onCropChange}
                    onCropEnd={onCropEnd}
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                    naturalWidth={naturalSize.width}
                    naturalHeight={naturalSize.height}
                  />
                </>
              )}

              {activeTool === 'lasso' && (
                <LassoTool
                  imageUrl={imageUrl}
                  points={lassoPoints}
                  onPointsChange={onLassoPointsChange}
                  onExport={onLassoExport}
                  originalFileName={file.name}
                  containerWidth={containerWidth}
                  containerHeight={containerHeight}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
